/**
 * Formulation Finder — Main Controller
 * Bootstraps the welcome screen and hands off to FormulationFinderEngine
 * (formulation-finder-engine.js) once the user clicks "Begin Consultation".
 * This file owns the hero only; everything past that is the engine's job.
 */

class FormulationFinder {
  /**
   * @param {HTMLElement} root - the .ff root element for this section instance
   */
  constructor(root) {
    this.root = root;
    this.hero = root.querySelector('[data-ff-hero]');
    this.startButton = root.querySelector('[data-ff-start]');

    if (!this.hero || !this.startButton) {
      return;
    }

    this.engine = new FormulationFinderEngine(root);
    this.bindEvents();
  }

  bindEvents() {
    this.startButton.addEventListener('click', () => this.handleStart());
  }

  handleStart() {
    this.hero.classList.add('is-hidden');
    this.hero.setAttribute('aria-hidden', 'true');
    this.startButton.blur();

    this.root.dispatchEvent(
      new CustomEvent('ff:start', { bubbles: true, detail: { sectionId: this.root.dataset.sectionId } })
    );

    this.engine.start();

    // opacity:0 alone leaves the hero's full-viewport height in the layout,
    // pushing the quiz down behind a blank screen. Collapse it out of flow
    // once the fade transition finishes (with a fallback in case
    // transitionend never fires, e.g. prefers-reduced-motion).
    const collapse = () => {
      this.hero.hidden = true;
    };
    this.hero.addEventListener('transitionend', collapse, { once: true });
    window.setTimeout(collapse, 450);
  }
}

/**
 * Boot every Formulation Finder instance on the page.
 * Supports multiple sections and Shopify Theme Editor section re-renders.
 */
function initFormulationFinders(
  container = document
) {
  container
    .querySelectorAll('[data-ff-root]')
    .forEach((root) => {
      if (
        root.dataset.ffInitialized ===
        'true'
      ) {
        return;
      }

      const finder =
        new FormulationFinder(root);

      /*
       * Only mark the section as initialized
       * once the controller successfully created
       * its engine.
       */
      if (finder.engine) {
        root.dataset.ffInitialized =
          'true';
      }
    });
}
document.addEventListener('DOMContentLoaded', () => initFormulationFinders());

// Re-init on Theme Editor section load/reorder so editing the hero doesn't break the button.
document.addEventListener('shopify:section:load', (event) => initFormulationFinders(event.target));

document.addEventListener(
  'ff:results:show',
  () => {
    document
      .querySelectorAll(
        '.ff-after-results'
      )
      .forEach((section) => {
        section.classList.add(
          'is-visible'
        );
      });
  }
);